import {
  Box,
  IconButton,
  InputAdornment,
  styled,
  Typography,
} from '@mui/material';
import Tooltip, { tooltipClasses, TooltipProps } from '@mui/material/Tooltip';
import { FC, useCallback, useState } from 'react';

import { HideIcon, InfoIcon, ShowIcon } from 'src/components/icons';
import {
  FormTextField,
  formTextFieldLabelSx,
  FormTextFieldProps,
} from '../TextField';

type Props = Omit<FormTextFieldProps, 'type'> & { isTooltipVisible?: boolean };

const PasswordTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#C5D9E8',
    borderRadius: 7,
    color: '#356482',
    fontWeight: 500,
    maxWidth: 252,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
}));

export const FormPasswordField: FC<Props> = ({
  isTooltipVisible = true,
  label,
  ...props
}) => {
  const [isPasswordVisible, setPasswordVisible] = useState(false);

  const Label = useCallback(
    () => <Typography sx={{ ...formTextFieldLabelSx }}>{label}</Typography>,
    [label],
  );

  const LabelWithTooltip = useCallback(
    () => (
      <PasswordTooltip
        placement="right"
        title={
          <>
            <Typography
              color="inherit"
              sx={{
                color: '#260F26',
                fontSize: 10,
                fontWeight: 600,
                lineHeight: '14px',
              }}
            >
              Password must contain at least 3 of the following:
            </Typography>
            <Typography
              color="inherit"
              sx={{ fontSize: '10px', wordBreak: 'break-word' }}
            >
              Uppercase letters
              <br />
              Lowercase letters
              <br />
              Digits (0-9)
              <br />
              {'Special characters !@#$%^&*()_+-=[]{};\':"|,.<>\\/?'}
            </Typography>
          </>
        }
      >
        <Box sx={{ display: 'inline-flex', flexDirection: 'row' }}>
          <Label />
          <InfoIcon
            sx={{
              color: '#356482',
              fontSize: 15,
              marginLeft: '5px',
              width: '20px',
            }}
          />
        </Box>
      </PasswordTooltip>
    ),
    [],
  );

  const ShowHidePasswordIcon = useCallback(
    () => (
      <InputAdornment position="end">
        <IconButton
          aria-label="toggle password visibility"
          onClick={() => {
            setPasswordVisible(!isPasswordVisible);
          }}
          onMouseDown={(event) => {
            event.preventDefault();
          }}
          edge="end"
        >
          {isPasswordVisible ? (
            <HideIcon sx={{ color: '#9AB1C0', fontSize: 20 }} />
          ) : (
            <ShowIcon sx={{ color: '#9AB1C0', fontSize: 20 }} />
          )}
        </IconButton>
      </InputAdornment>
    ),
    [isPasswordVisible],
  );

  return (
    <FormTextField
      {...props}
      Label={isTooltipVisible ? <LabelWithTooltip /> : <Label />}
      InputProps={{ endAdornment: <ShowHidePasswordIcon /> }}
      type={isPasswordVisible ? 'text' : 'password'}
    />
  );
};
